/**
 * Every side of an invitation: the sending side (member lists, invite dialogs, in project,
 * dataspace and department settings), `link.*` (what the inviter is told when no mail went out),
 * `inbox.*` and `banner.*` (the recipient's list in Account, and the count the user menu and the
 * home pages show), and `accept.*`, the page the mailed link lands on.
 *
 * `errors.*` is keyed by the server's error codes and looked up dynamically, so
 * those key names are a contract with the API rather than free-form. The accept
 * page's own errors stay out of that table, under `accept.errors.*`, and the inbox's under
 * `inbox.errors.*`.
 */
export default {
	invitations: {
		title: "Mitglieder",
		actions: {
			invite: "Mitglied einladen",
			send: "Einladung senden",
			resend: "Einladung erneut senden",
			cancel: "Einladung zurücknehmen",
			dismiss: "Abgelehnte Einladung entfernen",
		},
		dialogTitle: "Mitglied einladen",
		you: "Sie",
		fields: {
			email: "E-Mail-Adresse",
			emailInvalid: "Geben Sie eine gültige E-Mail-Adresse ein.",
			role: "Rolle",
		},
		pending: {
			tag: "Offen",
		},
		declined: {
			tag: "Abgelehnt",
		},
		invitedBy: "Eingeladen von {name}",
		expiry: {
			in: "Läuft in {n} Tag ab | Läuft in {n} Tagen ab",
			expired: "Abgelaufen",
		},
		confirm: {
			cancel: "Diese Einladung zurücknehmen?",
			remove: "{name} aus diesem Projekt entfernen?",
		},
		members: {
			empty: "Noch keine Mitglieder.",
		},
		errors: {
			already_member: "Dieser Benutzer ist bereits Mitglied.",
			already_invited:
				"Für diese E-Mail-Adresse ist bereits eine Einladung offen. Nehmen Sie sie zurück oder senden Sie sie erneut.",
			self_invite: "Sie können sich nicht selbst einladen.",
			not_a_department_member:
				"In diesen Space können nur Mitglieder seiner Abteilung eingeladen werden. Laden Sie die Person zuerst in die Abteilung ein.",
			invitation_mail_disabled:
				"Der Einladungsversand per E-Mail ist ausgeschaltet oder dieser Server kann keine E-Mails senden. Eine Einladung kann daher nicht erneut gesendet werden. Nehmen Sie sie zurück und laden Sie erneut ein, um einen Link zum Weitergeben zu erhalten.",
			self_role_change: "Sie können Ihre eigene Rolle nicht ändern.",
			email_mismatch: "Diese Einladung wurde an eine andere E-Mail-Adresse gesendet.",
			expired: "Diese Einladung ist abgelaufen.",
			generic: "Einladung konnte nicht gesendet werden.",
		},
		status: {
			sent: "Einladung gesendet.",
			created: "Einladung erstellt. Es wurde keine E-Mail gesendet.",
			resent: "Einladung erneut gesendet.",
			cancelled: "Einladung zurückgenommen.",
			dismissed: "Abgelehnte Einladung entfernt.",
		},
		link: {
			title: "Es wurde keine E-Mail gesendet",
			body: "Der Einladungsversand per E-Mail ist ausgeschaltet oder dieser Server kann keine E-Mails senden. Geben Sie diesen Link selbst an {email} weiter.",
			label: "Einladungslink",
			copy: "Link kopieren",
			copied: "Link kopiert.",
			copyFailed:
				"Kopieren nicht möglich. Markieren Sie den Link und kopieren Sie ihn von Hand.",
			once: "Der Link wird nur jetzt angezeigt und lässt sich nicht erneut abrufen. Er kann einmal verwendet werden, von einem Konto mit dieser E-Mail-Adresse.",
			done: "Fertig",
		},
		inbox: {
			title: "Einladungen",
			description:
				"Wozu Sie eingeladen wurden. Einladungen werden nach der E-Mail-Adresse aufgelistet, die Ihr Kontodienst bestätigt hat, sodass Sie sie hier ohne den Link aus der E-Mail beantworten können.",
			empty: "Sie haben keine offenen Einladungen.",
			emptyNote: "Eine neue erscheint hier, sobald sie gesendet wird.",
			label: "Offene Einladungen",
			column: {
				invitation: "Einladung",
				role: "Rolle",
			},
			kind: {
				project: "Projekt",
				dataspace: "Dataspace",
				department: "Abteilung",
			},
			meta: "Eingeladen von {name} \u00b7 {expiry}",
			accept: "Annehmen",
			decline: "Ablehnen",
			acceptFor: "Einladung zu {name} annehmen",
			declineFor: "Einladung zu {name} ablehnen",
			declineConfirm: {
				title: "Einladung zu {name} ablehnen?",
				note: "{inviter} sieht, dass Sie abgelehnt haben. Die Einladung verschwindet aus Ihrer Liste, und Sie können erneut eingeladen werden.",
			},
			accepted: "Sie sind {name} als {role} beigetreten.",
			open: "{name} öffnen",
			declined: "Einladung zu {name} abgelehnt.",
			refresh: "Aktualisieren",
			unverified: {
				title: "Ihre E-Mail-Adresse ist nicht bestätigt",
				body: "Ihre E-Mail-Adresse ist bei Ihrem Kontodienst nicht bestätigt, daher können Einladungen an {email} hier weder angezeigt noch beantwortet werden. Eine Einladung bindet sich nur an eine vom Kontodienst bestätigte Adresse. Bestätigen Sie sie dort und melden Sie sich anschließend erneut an.",
				action: "Sicherheitseinstellungen",
			},
			errors: {
				emailNotVerified:
					"Ihre E-Mail-Adresse ist bei Ihrem Kontodienst nicht bestätigt, daher kann diese Einladung nicht beantwortet werden. Bestätigen Sie sie dort und melden Sie sich anschließend erneut an.",
				gone: "Diese Einladung ist nicht mehr gültig: Sie ist abgelaufen, wurde zurückgenommen oder anderswo beantwortet.",
				notADepartmentMember:
					"Dieser Space gehört zu einer Abteilung, deren Mitglied Sie nicht sind. Sobald Sie der Abteilung beigetreten sind, können Sie die Einladung annehmen.",
			},
		},
		banner: {
			title: "Sie haben {n} offene Einladung | Sie haben {n} offene Einladungen",
			action: "Einladungen ansehen",
		},
		accept: {
			title: "Projekteinladung",
			titleDataspace: "Dataspace-Einladung",
			titleDepartment: "Abteilungseinladung",
			invitedTo: "Sie wurden als {role} zu {project} eingeladen.",
			invitedToEmail: "Diese Einladung wurde an {email} gesendet.",
			expires: "Diese Einladung läuft am {date} ab.",
			errors: {
				notFound: "Einladung nicht gefunden.",
				gone: "Diese Einladung ist nicht mehr gültig.",
				emailMismatch:
					"Sie sind als anderer Benutzer angemeldet. Melden Sie sich ab und verwenden Sie zum Annehmen die eingeladene E-Mail-Adresse.",
				emailNotVerified:
					"Ihre E-Mail-Adresse ist noch nicht bestätigt. Bestätigen Sie sie im Kontodienst und melden Sie sich anschließend erneut an.",
			},
			actions: {
				accept: "Einladung annehmen",
				signInToAccept: "Zum Annehmen anmelden",
				signOut: "Abmelden",
			},
			status: {
				failed: "Einladung konnte nicht angenommen werden.",
			},
		},
	},
};
